import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { toast } from "@altimateai/lego";
import { EntityType } from "./types";

interface Props {
  onDelete: (entityId: string, type: EntityType) => Promise<unknown> | undefined;
  refetch: () => void;
}

interface DeleteParams {
  entityId: string;
  type: EntityType;
  title: string;
}

const useUserGroupEntityDelete = ({ onDelete, refetch }: Props) => {
  const [entityBeingDeleted, setEntityBeingDeleted] = useState<string | undefined>();
  const [entityType, setEntityType] = useState<EntityType | undefined>();

  const { mutate, isPending: isDeletingEntity } = useMutation({
    mutationFn: async ({ entityId, type }: DeleteParams) => {
      setEntityType(type);
      setEntityBeingDeleted(entityId);

      const result = onDelete(entityId, type);
      if (result instanceof Promise) {
        return await result;
      }
      return result;
    },
    onSuccess: (data: unknown, variables: DeleteParams) => {
      if (variables.type === "user") {
        toast({ description: `${variables.title} deleted successfully` });
      } else if (variables.type === "rule") {
        toast({ description: "Access rule deleted successfully" });
      } else if (variables.type === "ownership") {
        toast({ description: "Ownership deleted successfully" });
      } else if (variables.type === "usergroup") {
        toast({ description: "Team deleted successfully" });
      }
      const response = data as { warning?: string } | undefined;
      if (response?.warning) {
        toast({ description: response.warning });
      }
      refetch();
    },
    onError: (err: Error) => {
      toast({ description: `Could not delete: ${err}`, variant: "destructive" });
    },
    onSettled: () => {
      setEntityBeingDeleted(undefined);
    },
  });

  const deleteEntity = (entityId: string, type: EntityType, title: string) => {
    mutate({ entityId, type, title });
  };

  return {
    deleteEntity,
    isDeletingEntity,
    entityBeingDeleted,
    entityType,
  };
};

export default useUserGroupEntityDelete;
